import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import PolicyAcceptance from '../components/PolicyAcceptance';
import { recordPolicyAcceptance } from '../utils/policyAcceptance';
import { setEmployerInfo, sanitizeInput } from '@/utils/authUtils';
import { v4 as uuidv4 } from 'uuid';

const EmployerLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [cookieAccepted, setCookieAccepted] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Sanitize input
      const sanitizedEmail = sanitizeInput(email);
      
      if (!sanitizedEmail || !password) {
        toast.error('Please enter both email and password');
        setLoading(false);
        return;
      }
      
      if (!termsAccepted || !privacyAccepted || !cookieAccepted) {
        toast.error('Please accept all policies to continue');
        setLoading(false);
        return;
      }
      
      try {
        await recordPolicyAcceptance(
          sanitizedEmail,
          'employer',
          termsAccepted,
          privacyAccepted,
          cookieAccepted
        );
      } catch (error) {
        console.error('Failed to record policy acceptances:', error);
      }

      if (!supabase || !supabase.auth || typeof supabase.auth.signInWithPassword !== 'function') {
        console.warn('Supabase auth not initialized properly, using development mode login');
        
        const mockEmployers = [
          { email: 'employer1@example.com', password: 'password123', companyName: 'ACME Corp' },
          { email: 'employer2@example.com', password: 'password123', companyName: 'Globex Inc' },
        ];
        
        const employer = mockEmployers.find(emp => 
          emp.email === sanitizedEmail && emp.password === password
        );
        
        if (employer) {
          setEmployerInfo(sanitizedEmail, employer.companyName);
          
          setTimeout(() => {
            toast.success('Login successful');
            navigate('/dashboard');
            setLoading(false);
          }, 800);
        } else {
          toast.error('Invalid credentials');
          setLoading(false);
        }
        return;
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: sanitizedEmail,
        password,
      });

      if (error) {
        console.error('Login error:', error);
        toast.error(error.message || 'Authentication failed');
      } else {
        try {
          const { data: employerData, error: employerError } = await supabase
            .from('employers')
            .select('*')
            .eq('email', sanitizedEmail)
            .maybeSingle();
            
          if (employerError) {
            console.error('Error fetching employer data:', employerError);
            toast.error('Login successful but failed to fetch employer data');
          } else if (employerData) {
            setEmployerInfo(sanitizedEmail, employerData.company_name);
          }
        } catch (queryError) {
          console.error('Error in employer query:', queryError);
          toast.error('Error retrieving employer information');
        }
        
        toast.success('Login successful');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An unexpected error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-2xl font-bold">Employer Login</CardTitle>
          <CardDescription className="text-center">
            Sign in to access your TrueApply dashboard
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleLogin}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <a href="#" className="text-sm text-blue-600 hover:text-blue-500">
                  Forgot password?
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Lock className="h-4 w-4 text-gray-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>
            <PolicyAcceptance
              termsChecked={termsAccepted}
              privacyChecked={privacyAccepted}
              cookieChecked={cookieAccepted}
              onTermsChange={setTermsAccepted}
              onPrivacyChange={setPrivacyAccepted}
              onCookieChange={setCookieAccepted}
              className="pt-2"
            />
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || !termsAccepted || !privacyAccepted || !cookieAccepted}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
            {(!termsAccepted || !privacyAccepted || !cookieAccepted) && (
              <p className="text-xs text-orange-500 text-center">
                Please accept all policies to continue
              </p>
            )}
            <p className="text-center text-sm text-gray-500">
              Contact administrator if you need access.
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default EmployerLogin;
